import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PasswordStrength } from '@/components/ui/password-strength';

type PasswordChangeMode = 'default' | 'account-confirmation';

interface RequiredPasswordChangeModalProps {
  isOpen: boolean;
  onConfirm: (newPassword: string) => Promise<void>;
  isLoading?: boolean;
  userName?: string;
  mode?: PasswordChangeMode;
}

export const RequiredPasswordChangeModal: React.FC<RequiredPasswordChangeModalProps> = ({
  isOpen,
  onConfirm,
  isLoading = false,
  userName,
  mode = 'default',
}) => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Determinar el prefijo de las cadenas de traducción según el modo
  const translationPrefix =
    mode === 'account-confirmation'
      ? 'pages.login.requiredPasswordChange.accountConfirmation'
      : 'pages.login.requiredPasswordChange';

  // Función para verificar si la contraseña cumple con todos los requisitos
  const isPasswordValid = (password: string): boolean => {
    return (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password)
    );
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!newPassword) {
      errors.newPassword = t('validation.required');
    } else {
      // Validar longitud mínima
      if (newPassword.length < 8) {
        errors.newPassword = t('validation.passwordMinLength');
      }
      // Validar que contenga al menos una mayúscula
      else if (!/[A-Z]/.test(newPassword)) {
        errors.newPassword = t('validation.passwordUppercase');
      }
      // Validar que contenga al menos una minúscula
      else if (!/[a-z]/.test(newPassword)) {
        errors.newPassword = t('validation.passwordLowercase');
      }
      // Validar que contenga al menos un número
      else if (!/[0-9]/.test(newPassword)) {
        errors.newPassword = t('validation.passwordNumber');
      }
      // Validar que contenga al menos un carácter especial
      else if (!/[^A-Za-z0-9]/.test(newPassword)) {
        errors.newPassword = t('validation.passwordSpecial');
      }
    }

    if (!confirmPassword) {
      errors.confirmPassword = t('validation.required');
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = t('validation.passwordMismatch');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await onConfirm(newPassword);
      // No cerramos el modal aquí, el padre se encargará de cerrarlo
    } catch {
      // Error handling is done in the parent component
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop con efecto focal */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />

      {/* Modal centrado */}
      <div className="relative bg-brand-white rounded-lg shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 ease-out scale-100 opacity-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-brand-gray-dark sentence-case">
              {t(`${translationPrefix}.title`)}
            </h3>
          </div>
          {/* No botón de cerrar ya que es obligatorio */}
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="text-center">
            <p className="text-brand-gray-dark sentence-case leading-relaxed">
              {userName
                ? t(`${translationPrefix}.descriptionWithName`, { name: userName })
                : t(`${translationPrefix}.description`)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-gray-dark">
              {t(`${translationPrefix}.newPassword`)}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder={t(`${translationPrefix}.newPasswordPlaceholder`)}
                className="w-full pr-10"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {newPassword && <PasswordStrength password={newPassword} />}
            {validationErrors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.newPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-gray-dark">
              {t(`${translationPrefix}.confirmPassword`)}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder={t(`${translationPrefix}.confirmPasswordPlaceholder`)}
                className="w-full pr-10"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                disabled={isLoading}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {validationErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.confirmPassword}</p>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={handleSubmit}
            disabled={
              isLoading ||
              !newPassword ||
              !confirmPassword ||
              newPassword !== confirmPassword ||
              !isPasswordValid(newPassword)
            }
            className={`bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 sentence-case ${
              isLoading ||
              !newPassword ||
              !confirmPassword ||
              newPassword !== confirmPassword ||
              !isPasswordValid(newPassword)
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
            {isLoading ? t('common.saving') : t(`${translationPrefix}.confirm`)}
          </button>
        </div>
      </div>
    </div>
  );
};
